import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, apiErrorMessage } from '../api/client';
import StatusPill from '../components/StatusPill';
import DocumentViewer, { DoseRow, SourceDocument } from '../components/DocumentViewer';
import IssueDH680Dialog from '../components/IssueDH680Dialog';
import { DH680Summary, dh680Filename, downloadDH680 } from '../api/dh680';

interface MatrixRow {
  vaccine: string;
  group: string;
  required_doses: string;
  found_doses: number;
  doses_needed?: number | null;
  earliest_next_dose?: string | null;
  rule_clause?: string;
  rule_clause_code?: string | null;
  status: string;
}

interface ReviewItem {
  code: string;
  record_id?: string | null;
  label?: string | null;
  antigen_group?: string | null;
  detail?: string | null;
}

interface EvaluatedDose {
  record_id: string;
  label: string;
  original_label?: string | null;
  cvx?: string | null;
  date?: string | null;
  age_at_dose?: string | null;
  verified?: boolean;
  rejected?: boolean;
  antigens?: Array<{
    group: string;
    valid: boolean;
    dose_number?: number | null;
    reason?: string | null;
    detail?: string | null;
    grace_applied?: boolean;
  }>;
}

interface Note {
  note_id: string;
  note_text: string;
  category: string;
  created_at: string;
  author: string;
  author_role: string;
}

interface Detail {
  student: Record<string, any>;
  guardian: Record<string, any>;
  compliance: {
    status: string;
    v2_status?: string | null;
    rule_set_id?: string | null;
    data_basis?: string;
    deficiency: string | null;
    audit_matrix: MatrixRow[];
    review_items?: ReviewItem[];
    doses?: EvaluatedDose[];
    raw: any;
    provisional?: any;
  };
  nurse_notes: Note[];
  vaccination_records?: DoseRow[];
  documents?: SourceDocument[];
  exemptions?: Array<Record<string, any>>;
  immunity_evidence?: Array<Record<string, any>>;
  dh680_current?: DH680Summary | null;
  dh680_history?: DH680Summary[];
}

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
};

const signedFmt = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  timeZone: 'America/New_York',
});

const uploadedFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

const StudentDetailPage: React.FC = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [noteText, setNoteText] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/students/${studentId}`);
      setDetail(res.data as Detail);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load student.'));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Download the Florida SHOTS transcription worksheet for this student.
   *
   * The worksheet is laid out in registry data-entry order so the nurse can key
   * the record into SHOTS from paper, using her own view-only access, without
   * reading a screen while typing. The reason string is recorded in the audit
   * trail alongside the export.
   */
  const printWorksheet = async () => {
    if (!studentId) return;
    setBusy(true);
    setActionMsg(null);
    try {
      const res = await api.get(`/api/admin/exports/worksheet/${studentId}`, {
        params: { reason: 'Florida SHOTS manual entry — single student' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data as BlobPart], { type: 'application/pdf' }),
      );
      // Open in a new tab so the nurse can print directly; the browser's PDF
      // viewer is a better print path than forcing a download.
      window.open(url, '_blank', 'noopener');
      setActionMsg('Worksheet generated.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not generate worksheet.'));
    } finally {
      setBusy(false);
    }
  };

  /** Open or save a DH 680 (current when certificateId is null). */
  const getPdf = async (
    cert: DH680Summary,
    certificateId: string | null,
    mode: 'open' | 'download',
  ) => {
    if (!studentId || !detail) return;
    try {
      await downloadDH680(
        studentId,
        certificateId,
        mode,
        dh680Filename(detail.student.last_name, detail.student.first_name, cert.serial),
      );
    } catch (err) {
      setActionMsg(apiErrorMessage(err, 'Could not get the DH 680 PDF.'));
    }
  };

  const onIssued = async (result: { certificate_id: string; serial: string }) => {
    setIssuing(false);
    setActionMsg(`DH 680 ${result.serial} issued.`);
    if (studentId) {
      try {
        await downloadDH680(studentId, result.certificate_id, 'open');
      } catch (err) {
        setActionMsg(apiErrorMessage(err, 'DH 680 issued, but the PDF could not be opened.'));
      }
    }
    load();
  };

  const flagIncomplete = async () => {
    if (!studentId) return;
    setBusy(true);
    setActionMsg(null);
    try {
      await api.post(`/api/admin/students/${studentId}/verify`, {
        verification_status: 'flagged',
        comments: null,
      });
      setActionMsg('Record flagged for follow-up.');
      load();
    } catch (err) {
      setActionMsg(apiErrorMessage(err, 'Action failed.'));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentId || !noteText.trim()) return;
    setBusy(true);
    setActionMsg(null);
    try {
      await api.post(`/api/admin/students/${studentId}/notes`, {
        note_text: noteText,
        category: noteCategory,
      });
      setNoteText('');
      setActionMsg('Note added.');
      load();
    } catch (err) {
      setActionMsg(apiErrorMessage(err, 'Failed to add note.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading student…</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!detail) return null;

  const { student, guardian, compliance, nurse_notes } = detail;
  const viewingDoc = (detail.documents ?? []).find((d) => d.document_id === viewingDocId);
  const current = detail.dh680_current ?? null;
  const history = detail.dh680_history ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          onClick={() => navigate('/directory')}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Return to Directory
        </button>
        <button
          onClick={printWorksheet}
          disabled={busy}
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          title="Printable worksheet in Florida SHOTS data-entry order"
        >
          Print SHOTS worksheet
        </button>
      </div>

      <div
        role="status"
        className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <strong>Florida DOH Rule 64D-3.046 Clinical Verification:</strong> Verify every dose
        against the guardian-submitted document or Florida SHOTS before certifying a DH 680 or
        reporting. Unverified doses only count toward the provisional preview, never the
        authoritative status.
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-5 shadow-sm">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Student &amp; Guardian
          </h2>
          <h1 className="text-lg font-semibold text-slate-900">
            {student.first_name} {student.last_name}
          </h1>
          {student.short_id && (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500">Student Code:</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 font-mono text-xs font-bold text-blue-800 tracking-wider">
                {student.short_id}
              </span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(student.short_id);
                  setActionMsg('Student Code copied to clipboard.');
                }}
                className="text-xs text-blue-600 hover:underline"
                title="Copy Student Code"
              >
                Copy
              </button>
            </div>
          )}
          <dl className="mt-2 space-y-1 text-sm text-slate-600">
            <div>DOB: {student.date_of_birth}</div>
            <div>Grade: {student.grade_label}</div>
            <div>Student Code: <span className="font-mono font-semibold text-blue-700">{student.short_id || '—'}</span></div>
            <div>
              Guardian:{' '}
              {guardian.display_name || guardian.email || guardian.phone || (
                <span className="italic text-slate-400">No contact info</span>
              )}
              {guardian.verified_identity && (
                <span
                  className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                  title="Name checked against a government ID at sign-in"
                >
                  ID verified
                </span>
              )}
            </div>
            <div>
              Contact: {guardian.phone || '—'} · {guardian.email || '—'}
            </div>
            <div>Registry Barcode: {student.registry_id || '—'}</div>
            <div>Form Serial: {student.form_serial_number || '—'}</div>
            <div>State IMM ID: {student.state_imm_id || '—'}</div>
          </dl>
        </div>

        <div className="rounded-xl bg-white p-5 shadow-sm">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Florida DH 680 Status
          </h2>
          <StatusPill status={compliance.status} />
          {compliance.deficiency && (
            <p className="mt-2 text-sm text-slate-600">{compliance.deficiency}</p>
          )}
          {current ? (
            <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm">
              <p className="font-medium text-green-900">
                DH 680 issued — {current.part_label} — {current.serial} — signed by{' '}
                {current.signer_name} on{' '}
                {current.signed_at ? signedFmt.format(new Date(current.signed_at)) : '—'}
              </p>
              {current.part === 'B' && (
                <p className="mt-1 text-green-900">Expires {fmtDate(current.part_b_expiration)}</p>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => getPdf(current, null, 'open')}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  Open PDF
                </button>
                <button
                  type="button"
                  onClick={() => getPdf(current, null, 'download')}
                  className="rounded-md border border-blue-600 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                >
                  Download PDF
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-500">No DH 680 has been issued yet.</p>
          )}
          {history.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-slate-400">
              {history.map((c) => (
                <li key={c.certificate_id} className="flex items-center gap-2">
                  <span>
                    Voided {c.serial} — {c.part_label} — {c.signer_name} —{' '}
                    {c.signed_at ? signedFmt.format(new Date(c.signed_at)) : '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => getPdf(c, c.certificate_id, 'download')}
                    className="text-slate-500 underline hover:text-slate-700"
                  >
                    Download
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
            Vaccination Audit Matrix — FL Admin Code Rule 64D-3.046
          </h2>
          {compliance.rule_set_id && (
            <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">
              Rule Set: {compliance.rule_set_id}
            </span>
          )}
        </div>
        {compliance.review_items && compliance.review_items.length > 0 && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <div className="font-semibold">Clinical Review Items ({compliance.review_items.length}):</div>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {compliance.review_items.map((ri, idx) => (
                <li key={idx}>
                  <span className="font-mono font-semibold">{ri.code}</span>
                  {ri.detail ? ` — ${ri.detail}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Vaccine</th>
              <th className="px-3 py-2">Req'd Doses</th>
              <th className="px-3 py-2">Found</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Rule Clause / Earliest Next Dose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {compliance.audit_matrix.map((row) => (
              <tr key={row.group}>
                <td className="px-3 py-2 font-medium text-slate-800">{row.vaccine}</td>
                <td className="px-3 py-2 text-slate-600">{row.required_doses}</td>
                <td className="px-3 py-2 text-slate-600">{row.found_doses}</td>
                <td className="px-3 py-2">
                  <StatusPill status={row.status} />
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  <div>{row.rule_clause || '—'}</div>
                  {row.earliest_next_dose && (
                    <div className="mt-0.5 font-medium text-amber-700">
                      Earliest next dose: {row.earliest_next_dose}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SourceRecordsCards
        studentId={studentId || ''}
        documents={detail.documents ?? []}
        doses={detail.vaccination_records ?? []}
        evaluatedDoses={compliance.doses ?? []}
        onView={setViewingDocId}
        onReload={load}
        onMessage={setActionMsg}
      />
      {viewingDoc && studentId && (
        <DocumentViewer
          studentId={studentId}
          doc={viewingDoc}
          doses={detail.vaccination_records ?? []}
          onClose={() => setViewingDocId(null)}
        />
      )}
      {issuing && studentId && (
        <IssueDH680Dialog
          studentId={studentId}
          onClose={() => setIssuing(false)}
          onIssued={onIssued}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Nurse Clinical Action
          </h2>
          <p className="mb-3 text-xs text-slate-500">
            Compare the recorded doses with the guardian-submitted documents before verifying.
          </p>
          {actionMsg && (
            <div className="mb-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
              {actionMsg}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <button
              disabled={busy}
              onClick={() => setIssuing(true)}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              Verify Record &amp; Issue DH 680
            </button>
            <button
              disabled={busy}
              onClick={flagIncomplete}
              className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              Flag Incomplete — Contact Parent
            </button>
          </div>
        </div>

        <div className="rounded-xl bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Nurse Notes
          </h2>
          <form onSubmit={addNote} className="space-y-2">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
              placeholder="Add a clinical note…"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="flex items-center gap-2">
              <select
                value={noteCategory}
                onChange={(e) => setNoteCategory(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="general">General</option>
                <option value="compliance_followup">Compliance Follow-up</option>
                <option value="exemption_review">Exemption Review</option>
              </select>
              <button
                type="submit"
                disabled={busy || !noteText.trim()}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Add Note
              </button>
            </div>
          </form>

          <ul className="mt-4 space-y-3">
            {nurse_notes.length === 0 && (
              <li className="text-sm text-slate-400">No notes yet.</li>
            )}
            {nurse_notes.map((n) => (
              <li key={n.note_id} className="rounded-md bg-slate-50 p-3 text-sm">
                <p className="text-slate-700">{n.note_text}</p>
                <p className="mt-1 text-xs text-slate-400">
                  {n.author} ({n.author_role}) · {n.category} ·{' '}
                  {new Date(n.created_at).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/** "Guardian-submitted records" + "Recorded doses" cards (plan §13.7, WI-01, WI-16). */
const SourceRecordsCards: React.FC<{
  studentId: string;
  documents: SourceDocument[];
  doses: DoseRow[];
  evaluatedDoses: EvaluatedDose[];
  onView: (documentId: string) => void;
  onReload: () => void;
  onMessage: (msg: string | null) => void;
}> = ({ studentId, documents, doses, evaluatedDoses, onView, onReload, onMessage }) => {
  const [busyDoseId, setBusyDoseId] = useState<string | null>(null);
  const evalByRecordId = new Map(evaluatedDoses.map((d) => [d.record_id, d]));

  const verifyOne = async (recordId: string) => {
    if (!studentId) return;
    setBusyDoseId(recordId);
    try {
      await api.post(`/api/admin/students/${studentId}/doses/${recordId}/verify`, {
        action: 'verify',
        verification_source: 'card_review',
      });
      onMessage('Dose verified.');
      onReload();
    } catch (err) {
      onMessage(apiErrorMessage(err, 'Could not verify dose.'));
    } finally {
      setBusyDoseId(null);
    }
  };

  const rejectOne = async (recordId: string) => {
    if (!studentId) return;
    const reason = window.prompt('Reason for rejecting this dose (required for audit):', 'Not supported by uploaded document');
    if (!reason || !reason.trim()) return;
    setBusyDoseId(recordId);
    try {
      await api.post(`/api/admin/students/${studentId}/doses/${recordId}/verify`, {
        action: 'reject',
        reason: reason.trim(),
        verification_source: 'card_review',
      });
      onMessage('Dose rejected.');
      onReload();
    } catch (err) {
      onMessage(apiErrorMessage(err, 'Could not reject dose.'));
    } finally {
      setBusyDoseId(null);
    }
  };

  const verifyAllDated = async () => {
    if (!studentId) return;
    const eligible = doses.filter((d) => !d.rejected && !d.verified && !!d.date_administered);
    if (eligible.length === 0) return;
    setBusyDoseId('all');
    try {
      await api.post(`/api/admin/students/${studentId}/doses/verify-all`, {
        record_ids: eligible.map((d) => d.record_id),
        verification_source: 'card_review',
      });
      onMessage(`Verified ${eligible.length} dose(s).`);
      onReload();
    } catch (err) {
      onMessage(apiErrorMessage(err, 'Could not verify all doses.'));
    } finally {
      setBusyDoseId(null);
    }
  };

  const unverifiedDatedCount = doses.filter(
    (d) => !d.rejected && !d.verified && !!d.date_administered,
  ).length;

  return (
    <>
      <div className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
          Guardian-submitted records
        </h2>
        {documents.length === 0 ? (
          <p className="text-sm text-slate-500">
            No documents on file. Records entered before document storage was enabled, or typed in
            manually, have no source document.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {documents.map((d) => (
              <li key={d.document_id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="text-slate-700">
                  Uploaded {d.uploaded_at ? uploadedFmt.format(new Date(d.uploaded_at)) : '—'} ·{' '}
                  {d.page_count} page{d.page_count === 1 ? '' : 's'} · {d.dose_count} dose
                  {d.dose_count === 1 ? '' : 's'}
                </span>
                <button
                  type="button"
                  onClick={() => onView(d.document_id)}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  View
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
            Recorded doses ({doses.length})
          </h2>
          {unverifiedDatedCount > 0 && (
            <button
              type="button"
              disabled={busyDoseId !== null}
              onClick={verifyAllDated}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Verify all dated ({unverifiedDatedCount})
            </button>
          )}
        </div>
        {doses.length === 0 ? (
          <p className="text-sm text-slate-500">No doses recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Vaccine</th>
                  <th className="px-3 py-2">Dose</th>
                  <th className="px-3 py-2">Date / Age</th>
                  <th className="px-3 py-2">ACIP Verdict</th>
                  <th className="px-3 py-2">Verification</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {doses.map((r) => {
                  const hasDoc =
                    !!r.source_document_id &&
                    documents.some((d) => d.document_id === r.source_document_id);
                  const ev = evalByRecordId.get(r.record_id);
                  const invalidAntigens = (ev?.antigens ?? []).filter((a) => !a.valid);
                  return (
                    <tr key={r.record_id} className={r.rejected ? 'bg-red-50/40 line-through text-slate-400' : ''}>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        <div>{r.vaccine_name}</div>
                        {r.vaccine_name_original && r.vaccine_name_original !== r.vaccine_name && (
                          <div className="text-xs font-normal text-slate-500">
                            Card: {r.vaccine_name_original}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{r.dose_number}</td>
                      <td className="px-3 py-2 text-slate-600">
                        <div>{r.date_administered || '—'}</div>
                        {ev?.age_at_dose && (
                          <div className="text-xs text-slate-500">Age: {ev.age_at_dose}</div>
                        )}
                        {r.source_calendar !== 'gregorian' && r.date_administered_raw && (
                          <div className="text-xs text-slate-400">
                            {r.date_administered_raw} ({r.source_calendar})
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {invalidAntigens.length > 0 ? (
                          <div className="space-y-0.5 text-red-700">
                            {invalidAntigens.map((ia, idx) => (
                              <div key={idx}>
                                <span className="font-semibold">{ia.group}:</span> {ia.reason}
                                {ia.detail ? ` (${ia.detail})` : ''}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-emerald-700 font-medium">Valid</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.rejected ? (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800">
                            Rejected{r.rejected_reason ? `: ${r.rejected_reason}` : ''}
                          </span>
                        ) : r.verified ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
                            Verified
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                            Unverified
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {hasDoc ? (
                          <button
                            type="button"
                            onClick={() => onView(r.source_document_id as string)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View page
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          {!r.verified && !r.rejected && !!r.date_administered && (
                            <button
                              type="button"
                              disabled={busyDoseId !== null}
                              onClick={() => verifyOne(r.record_id)}
                              className="rounded bg-emerald-600 px-2 py-1 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Verify
                            </button>
                          )}
                          {!r.rejected && (
                            <button
                              type="button"
                              disabled={busyDoseId !== null}
                              onClick={() => rejectOne(r.record_id)}
                              className="rounded border border-red-300 px-2 py-1 font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default StudentDetailPage;
